import React from 'react'
import FormRowWrapper from './FormRowWrapper/FormRowWrapper'
import { Checkbox, CheckboxGroup } from 'react-checkbox-group'
import { RadioGroup, Radio } from 'react-radio-group'

import { Editor } from '@tinymce/tinymce-react'
import settings from '../../../settings'
import tinyMCELocale from '../../common/tinyMCELocale/fa_IR.js.file'
import Api from '../../../api/api'
import { getErrors } from '../../../helpers/utility'

import AsyncSelect from 'react-select/lib/Async'

// ────────────────────────────────────────────────────────────────────────────────

export const genericSelectInput = ({
  title,
  name,
  input,
  meta: { touched, error },
  options = [],
}) => {
  let hasError = touched && error !== undefined

  return (
    <FormRowWrapper title={title} fieldName={name} hasError={hasError} error={error}>
      <select {...input}>
        <option value={undefined} />

        {options.map(option => (
          <option key={option.id} value={option.id}>
            {option.title}
          </option>
        ))}
      </select>
    </FormRowWrapper>
  )
}

// ────────────────────────────────────────────────────────────────────────────────

export const checkboxInput = ({
  input,
  meta: { touched, error },
  title,
  name,
  options = [],
  arrayOutput = false,
}) => {
  let hasError = touched && error !== undefined
  const value = input.value || typeof input.value === typeof true
    ? Array.isArray(input.value)
      ? input.value
      : [input.value]
    : arrayOutput? [] : input.value

  const changeHandler = value => {

    const newValue = !arrayOutput && value.length === 1 ? value[0] : value

    return input.onChange(newValue)
  }

  // never pass anything but onChange and value to CheckboxGroup !!!
  return (
    <FormRowWrapper title={title} fieldName={name} hasError={hasError} error={error}>
      <CheckboxGroup checkboxDepth={2} onChange={changeHandler} value={value}>
        {options.map(option => (
          <label key={option.id} className="checkbox-label">
            <Checkbox value={option.id} />
            {option.title}
          </label>
        ))}
      </CheckboxGroup>
    </FormRowWrapper>
  )
}

// ────────────────────────────────────────────────────────────────────────────────

export const fileInput = ({
  input,
  meta: { touched, error },
  placeholder = 'فایل',
  title,
  name,
  showImagePreview,
  showLinkPreview,
  accept,
}) => {
  let hasError = touched && error !== undefined
  const handleChange = event => input.onChange(event.target.files)

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  }

  const src = Boolean(input.value) && isValidUrl(input.value) ? input.value : null

  delete input.value

  let className = src && showImagePreview? "has-image-preview" : ""
  className += src && showLinkPreview? " has-link-preview" : ""

  const rest = accept? { accept } : {}

  return (
    <FormRowWrapper title={title} fieldName={name} hasError={hasError} error={error} className={className}>
      <input {...input} onChange={handleChange} type="file" placeholder={placeholder} {...rest} />
      {src && showImagePreview && <img src={src} alt="" className="image-file-preview" />}
      {src && showLinkPreview && <a className="file-link-preview" href={src} target="_blank">
       لینک فایل آپلود شده
      </a>}
    </FormRowWrapper>
  )
}

// ────────────────────────────────────────────────────────────────────────────────

export const genericRadioButtonGroup = ({
  title,
  name,
  input,
  meta: { touched, error },
  options,
  ...custom,
}) => {
  let hasError = touched && error !== undefined

  return (
    <FormRowWrapper title={title} fieldName={name} hasError={hasError} error={error} {...custom}>
      <RadioGroup name={name} {...input} selectedValue={input.value}>
        {options.map(option => (
          <label className="radio-button-label" key={option.id}>
            <Radio value={option.id} />
            {option.title}
          </label>
        ))}
      </RadioGroup>
    </FormRowWrapper>
  )
}

// ────────────────────────────────────────────────────────────────────────────────

export const generalNumberInput = ({
  input,
  meta: { touched, error },
  title,
  fieldName,
  ...custom
}) => {
  let hasError = touched && error !== undefined
  return (
    <FormRowWrapper title={title} fieldName={fieldName} hasError={hasError} error={error}>
      <input type="number" {...input} {...custom} />
    </FormRowWrapper>
  )
}

// ────────────────────────────────────────────────────────────────────────────────

export const generalTextInput = ({
  input,
  meta: { touched, error },
  title,
  fieldName,
  ...custom
}) => {
  let hasError = touched && error !== undefined
  return (
    <FormRowWrapper title={title} fieldName={fieldName} hasError={hasError} error={error}>
      <input type="text" {...input} {...custom} />
    </FormRowWrapper>
  )
}

// ────────────────────────────────────────────────────────────────────────────────

export const generalUrlInput = ({
  input,
  meta: { touched, error },
  title,
  fieldName,
  ...custom
}) => {
  let hasError = touched && error !== undefined
  return (
    <FormRowWrapper title={title} fieldName={fieldName} hasError={hasError} error={error}>
      <input type="url" {...input} {...custom} />
    </FormRowWrapper>
  )
}

// ────────────────────────────────────────────────────────────────────────────────

function handleTinyMCEImageUpload(image, onSuccess, onFailure) {
  // create the form data
  const formData = new FormData()
  formData.append('image', image.blob(), image.filename())

  // handlers
  const failHandler = ({ data }) => {
    if (data.errors) {
      onFailure(getErrors(data.errors))
      return
    }

    onFailure('اشکالی نامشخص رخ داد.')
  }

  const successHandler = ({ data }) => {
    if (data.status < 400) {
      onSuccess(data.data.image.image)
      return
    }

    failHandler(data)
  }

  // api call
  Api.genericImageUpload(formData)
    .then(successHandler)
    .catch(failHandler)
}

export const wysiwygInput = ({ input, name, title, meta: { touched, error } }) => {
  return (
    <FormRowWrapper
      title={title}
      fieldName={name}
      hasError={touched && error !== undefined}
      error={error}
    >
      {/* never use content props instead of value in TinyMCE it does not work!  */}
      <Editor
        value={input.value}
        initialValue={input.value}
        onEditorChange={input.onChange}
        onFocus={input.onFocus}
        onBlur={input.onBlur}
        apiKey={settings.tinyMCEApiKey}
        init={{
          min_height: 300,
          min_width: 600,
          width: parseInt(window.innerWidth * 0.55, 10),
          branding: false,
          plugins: 'image table',
          selector: 'textarea',
          resize: 'both',
          directionality: 'rtl',
          language: 'fa_IR',
          language_url: tinyMCELocale,
          images_upload_handler: handleTinyMCEImageUpload,
        }}
      />
    </FormRowWrapper>
  )
}

export const AsyncAutoCompleteSelect = ({
  input,
  name,
  title,
  placeholder,
  meta: { touched, error },
  // react select props
  cacheOptions,
  loadOptions,
  styles = {},
  ...custom
}) => {

  const stylesToPass = {
    ...styles
  }

  return (<FormRowWrapper
    title={title}
    fieldName={name}
    hasError={touched && error !== undefined}
    error={error}
    // className='w-100'
  >
    <AsyncSelect
      styles={stylesToPass}
      loadOptions={loadOptions}
      // cacheOptions
      value={input.value}
      onChange={(val) => input.onChange(val) }
      onBlur={() => input.onBlur(input.value)}
      isRtl={true}
      isSearchable={true}
      placeholder={placeholder || 'انتخاب'}
      loadingMessage={() => 'در حال بارگزاری...'}
      classNamePrefix="react-select"
      {...custom}
    />
  </FormRowWrapper>)
}


export const genericTextarea = ({ input, meta: { touched, error }, title, ...custom }) => {
  let hasError = touched && error !== undefined
  return (
    <FormRowWrapper
      title={title}
      fieldName={input.name}
      hasError={hasError}
      error={error}
    >
      <textarea
        rows={3}
        {...input}
        {...custom}
      />
    </FormRowWrapper>
  )
}



// WEBPACK FOOTER //
// ./src/components/common/FormFields/Fields.js